/* eslint-disable react/no-danger */
import { useState, useEffect, SetStateAction, Dispatch } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { CompactCard } from '@core/blocks/edu-match/components';
import Dots from '@core/blocks/edu-match/components/Loading/Dots';
import { PiiFormSection } from '@core/blocks/edu-match/components/PiiFormSection';
import { validateInputs } from '@core/blocks/edu-match/components/PiiFormSection/validateInputs';
import useCheckScreen from '@core/blocks/eduResults/hooks/useCheckScreen';
import { LEAD_SUBMISSION_STEPS } from '@core/constants';
import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import useGradConversionEditableAsset from '@core/hooks/useGradConversionEditableAsset';
import useSchoolConfig from '@core/hooks/useSchoolConfig';
import { selectDcsDegrees } from '@core/reducers/dcsSlice';
// Reducer's selectors/actions
import { setHeclidMapEntry, selectProductCorrelationIdMap, selectHeclidMap } from '@core/reducers/eventingSlice';
import { selectAllInputs, selectInput, setInputs } from '@core/reducers/inputsSlice';
import { addSubmission, hideLeadDelivery } from '@core/reducers/matchesSlice';
import { selectTestResultById } from '@core/reducers/monarchSlice';
import { submitLead } from '@core/services/leadDelivery';
import { mobiusRedirect } from '@core/services/mobius';
import { PageActions } from '@core/services/newRelic';
import { getSchoolConfig } from '@core/services/schoolConfig';
// Components
import Icon from '@core/shared/components/Icon';
import PulsingDotLoader from '@core/shared/components/PulsingDotLoader';
import { VoyagerResult } from '@core/ts/results';
// Utils, Hooks & Services
import cn from '@core/utils/classNames';
import { phoneValidationFeatureFlagWrapper } from '@core/utils/functionWrappers/phoneValidationFeatureFlagWrapper';
import getToken from '@core/utils/getToken';
import { isUnderGraduate } from '@core/utils/isUnderGraduate';
import timer from '@core/utils/timer';

import { selectContactId } from '../../../../reducers/contactSlice';
import SubmissionStatus from '../../SubmissionStatus';
import TcpaSection from '../../TcpaSection';
// Styles
import styles from './PiiConfirmation.module.css';

const { ADDITIONAL_QUESTIONS, EXIT_STRATEGY, PII_CONFIRMATION } = LEAD_SUBMISSION_STEPS;
type PiiConfirmationProps = {
  setPiiLeadErrors: Dispatch<SetStateAction<{ field: string }[]>>;
  setEditMode: Dispatch<SetStateAction<boolean>>;
  piiLeadErrors: { field: string }[];
  step: string;
  setStep: Dispatch<SetStateAction<string>>;
  currentProgram: VoyagerResult;
  prqSchema: any;
  prqLoading: boolean;
  formErrored: () => void;
  editMode: boolean;
};

// Gets or previews lead info
const PiiConfirmation = ({
  step,
  setStep,
  currentProgram,
  prqSchema,
  prqLoading,
  setPiiLeadErrors,
  piiLeadErrors,
  setEditMode,
  formErrored,
  editMode,
}: PiiConfirmationProps) => {
  // Inputs State properties
  const inputs = useSelector(selectAllInputs);

  // Eventing State properties
  const heclidMap = useSelector(selectHeclidMap);
  const productCorrelationIdMap = useSelector(selectProductCorrelationIdMap);

  // local state
  const [tcpa, setTcpa] = useState(undefined);

  const [tcpaLoading, setTcpaLoading] = useState(true);
  const [tcpaErrorMsg, setTcpaErrorMsg] = useState<string | undefined>(undefined);
  const startTime = useSelector((state) => selectInput(state, 'startTime'));
  const formInputs = useSelector(selectAllInputs);
  const [submissionStatus, setSubmissionStatus] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const isHeclidReady = heclidMap[currentProgram?.program.id] !== undefined;
  const [formError, setFormError] = useState<{ field: string }[]>([]);

  // First Name State
  const [firstName, setFirstName] = useState(formInputs.firstName?.value);
  // Last Name State
  const [lastName, setLastName] = useState(formInputs.lastName?.value);
  // Email State
  const [email, setEmail] = useState(formInputs.email?.value);
  // Phone State
  const [phone, setPhone] = useState(formInputs.phone?.value);

  const [hasPII, setHasPII] = useState<boolean>(true);
  const flags = useFeatureFlags();
  // Action Dispatcher
  const dispatch = useDispatch();

  const contactId = useSelector(selectContactId);

  const schoolConfig = useSchoolConfig(currentProgram?.school?.id);

  const [degree] = useSelector(selectDcsDegrees);

  const isGrad = !isUnderGraduate(degree);

  const { isMobile } = useCheckScreen();

  const isMobileUndergrad = isMobile && !isGrad;

  const noPii = useSelector((state) => selectTestResultById(state, 'noPii'));
  const { CtaLabelEA, SlideoutTitleEA, SlideoutSubtitleEA, PiiTitleEA, CtaTitleEA, CtaSubtitleEA } =
    useGradConversionEditableAsset();

  const handleSubmit = async () => {
    setFormError([]);
    setIsSubmitting(true);

    const formErrors = await validateInputs(firstName, lastName, email, phone);
    if (formErrors.length) {
      setFormError(formErrors);
      setIsSubmitting(false);
      return;
    }

    dispatch(
      setInputs([
        { key: 'firstName', value: firstName },
        { key: 'lastName', value: lastName },
        { key: 'email', value: email },
        { key: 'phone', value: phone },
      ])
    );
    if (prqSchema) {
      setStep(ADDITIONAL_QUESTIONS);
      return;
    }

    const actionParams = {
      ...flags,
      anonymousId: window._Cohesion.anonymousId,
      correlationId: productCorrelationIdMap[currentProgram?.program?.id],
    };
    PageActions.LeadAttempt(actionParams);
    const leadInputs = {
      ...inputs,
      tcpaText: { key: 'tcpaText', value: schoolConfig?.data?.tcpa },
    };
    // get the synchronousLead flag to pass to submitLead function
    const syncLead = flags?.synchronousLead;
    const res = await phoneValidationFeatureFlagWrapper({
      wrappedFunction: () =>
        submitLead(
          currentProgram,
          leadInputs,
          heclidMap[currentProgram?.program?.id],
          productCorrelationIdMap[currentProgram?.program?.id],
          contactId,
          syncLead
        ),
      inputs: leadInputs,
      flag: !!flags?.phoneValidation,
    });
    if (res?.message === 'invalid_phone_line_type_intelligence') {
      setIsSubmitting(false);
      setEditMode(true);
      setPiiLeadErrors((state) => [...state, { field: 'phone' }]);
      setSubmissionStatus('');
      formErrored();
      return;
    }

    if (res?.message) {
      setIsSubmitting(false);
      const error = JSON.parse(res.message);
      const status = error?.message === 'qualifications_not_met' ? 'UNQUALIFIED' : 'ERROR';
      setSubmissionStatus(status);
      await timer(2000);
      dispatch(hideLeadDelivery());
    } else {
      PageActions.LeadSuccess(actionParams);
      dispatch(addSubmission(currentProgram));
      setStep(EXIT_STRATEGY);
    }
  };

  useEffect(() => {
    if (step === PII_CONFIRMATION)
      mobiusRedirect({
        idToken: getToken(),
        productCorrelationId: productCorrelationIdMap[currentProgram?.program?.id],
        link: currentProgram?.url,
        trackingContextOverride: {
          formatType: 'app',
          formatSubtype: 'grid',
        },
      }).then(({ heclid, error }) => {
        if (!error) {
          dispatch(setHeclidMapEntry({ key: currentProgram?.program?.id, value: heclid }));
        }
      });
  }, []);

  useEffect(() => {
    if (!inputs.firstName?.value || !inputs.lastName?.value || !inputs.email?.value || !inputs.phone?.value) {
      setEditMode(true);
    }
  }, [inputs]);

  useEffect(() => {
    if (!firstName || !lastName || !email || !phone) {
      setHasPII(false);
    }
    if (firstName && lastName && email && phone) {
      setHasPII(true);
    }
  }, [firstName, lastName, email, phone]);
  // when currentProgram is set use getSchoolConfig to get tcpa
  useEffect(() => {
    if (currentProgram) {
      const getTcpa = async () => {
        const schoolConfig = await getSchoolConfig(currentProgram?.school?.id);

        if (schoolConfig.error) {
          setTcpaErrorMsg('We ran into an issue! Please try again.');
          await timer(2500);
          dispatch(hideLeadDelivery());
          return;
        }

        setTcpa({ ...schoolConfig, hit: currentProgram });
        setTcpaLoading(false);
      };
      getTcpa();
    }
  }, [currentProgram]);

  if (tcpaLoading)
    return (
      <section className={styles.loadingContainer}>
        {tcpaErrorMsg ? (
          <h2>{tcpaErrorMsg}</h2>
        ) : (
          <>
            <h2>Loading...</h2>
            <Dots />
          </>
        )}
      </section>
    );

  const ctaText = isGrad || isMobileUndergrad ? 'Learn More' : 'Submit Info';

  const buttonText =
    isSubmitting || !isHeclidReady || prqLoading ? <PulsingDotLoader /> : <CtaLabelEA>{ctaText}</CtaLabelEA>;

  const gradHeading =
    noPii && isGrad
      ? "Add your contact information to show this school you're interested! You can even start your application on the next screen."
      : "Submit your contact information to show this school you're interested! You can even start your application on the next screen.";

  const piiTitle = flags?.noPii && isGrad ? 'Please add your info' : 'Before submitting, please confirm your info:';
  return submissionStatus ? (
    <SubmissionStatus status={submissionStatus} schoolName={currentProgram?.school?.name} />
  ) : (
    <form onSubmit={(e) => e.preventDefault()} data-tf-element-role="offer" className={styles.piiConfirmation}>
      <div className={styles.piiSection}>
        <div className={styles.title}>
          <Icon name="yes" color="--primary-700" size="8" />
          <h2>
            {' '}
            <SlideoutTitleEA> {isGrad ? 'Want to Learn More?' : 'Request Information'}</SlideoutTitleEA>
          </h2>
        </div>
        <p className={styles.subtitle}>
          <SlideoutSubtitleEA>
            {isGrad
              ? gradHeading
              : 'Great choice! Submit your information and we’ll share some exciting details on what to expect next.'}
          </SlideoutSubtitleEA>
        </p>

        <CompactCard hit={currentProgram} showClose={false} />

        <h4>
          <PiiTitleEA>
            {isGrad ? <>{piiTitle}</> : <>Before you submit, let&apos;s make sure your information is correct.</>}
          </PiiTitleEA>
        </h4>
        {/* View/Edit personal info */}
        <div className={styles.infoPanel}>
          <PiiFormSection
            program={currentProgram?.program}
            setEditMode={setEditMode}
            editMode={editMode}
            piiLeadErrors={piiLeadErrors}
            location="single-program"
            onSubmit={handleSubmit}
            firstName={firstName}
            lastName={lastName}
            email={email}
            phone={phone}
            setFirstName={setFirstName}
            setLastName={setLastName}
            setEmail={setEmail}
            setPhone={setPhone}
            formErrors={formError}
            setFormErrors={setFormError}
          />
        </div>

        <TcpaSection tcpa={tcpa ? [tcpa] : null} />
      </div>
      <div className={styles.submitBtnSection}>
        <p className={styles.ctaTitle}>
          <CtaTitleEA>Double check your information before submitting{isGrad ? <></> : <>&nbsp;👆</>}</CtaTitleEA>
        </p>
        <p className={styles.ctaSubtitle}>
          <CtaSubtitleEA>
            {isGrad ? <></> : <>Your information will never be shared with a school without your consent.</>}
          </CtaSubtitleEA>
        </p>
        <button
          className={cn(
            styles.submitInfoBtn,
            (!isHeclidReady || !hasPII || prqLoading) && styles.disabledSubmitInfoBtn
          )}
          disabled={!isHeclidReady || isSubmitting || !hasPII || prqLoading}
          id="reward_click_lds"
          data-testid="edumatch-leaddelivery-submit"
          data-tf-element-role="submit"
          onClick={handleSubmit}
          type="button"
        >
          {buttonText}
        </button>
      </div>
    </form>
  );
};

export default PiiConfirmation;
